import { Button, Flex, Heading, Text, useToast } from '@chakra-ui/react';
import { useWeb3React } from 'hooks/web3';
import { DeploymentData } from 'pages/Onboarding';
import { FC, useEffect, useState } from 'react';
import { FieldError, FormProvider, useForm } from 'react-hook-form';
import Parameters from './parameters';
import SelectTokens from './selectTokens';
import SetMetadata from './setMetadata';
import TxnError from 'components/toasts/TxnError';
import { yupResolver } from '@hookform/resolvers/yup';
import { ProgramToken } from 'types';
import { useTransactions } from 'store/transactions/useTransactions';
import { schema } from './formSchema';
import { useConnection } from 'store/connection';
import { VscLink } from 'react-icons/vsc';
import { useModal } from 'store/modals';
import { ModalType } from 'components/modals/types';
import { shortenURL } from 'utils/shortUrl';
import { IDeploymentForm } from './type';
import { useConfig } from 'store/config';
import { synchronizeFirebase } from 'utils/onboarding';
import SelectAludelTemplate from './selectAludelTemplate';
import TxnPendingApprovals from 'components/toasts/TxnPendingApprovals';

export type DeploymentFormErrors = {
  name?: FieldError | undefined;
  startTime?: FieldError | undefined;
  getStakingTokenUrl?: FieldError | undefined;
  rewardTokenAmount?: FieldError | undefined;
  unlockDays?: FieldError | undefined;
  scalingFloor?: FieldError | undefined;
  scalingCeiling?: FieldError | undefined;
  scalingDays?: FieldError | undefined;
};

export type BonusToken = ProgramToken & { amount: number };

type Props = {
  nextStep: () => void;
  deploymentData: DeploymentData;
  setDeploymentData: React.Dispatch<React.SetStateAction<DeploymentData>>;
};

const Deployment: FC<Props> = ({
  nextStep,
  deploymentData,
  setDeploymentData
}) => {
  const { account, chainId = 1 } = useWeb3React();
  const { tried } = useConnection();
  const [stakingToken, setStakingToken] = useState<ProgramToken>();
  const [rewardToken, setRewardToken] = useState<ProgramToken>();
  const [bonusTokens, setBonusTokens] = useState<BonusToken[]>([]);
  const [deployEnabled, setDeployEnabled] = useState(false);
  const [deployPending, setDeployPending] = useState(false);
  const { openModal } = useModal();
  const modal = useModal();
  const toast = useToast();
  const { crucibleFactoryAddress } = useConfig(chainId).config;
  const methods = useForm<IDeploymentForm>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
    defaultValues: {
      unlockDays: 1,
      scalingFloor: 1,
      scalingCeiling: 1,
      scalingDays: 1
    }
  });

  const {
    handleSubmit,
    register,
    formState: { errors }
  } = methods;

  const { deployAludel, fundAludel, fundBonusToken } = useTransactions();

  useEffect(() => {
    setDeployEnabled(!!stakingToken?.address && !!rewardToken?.address);
  }, [stakingToken, rewardToken]);

  const onDeploy = async (formData: IDeploymentForm) => {
    setDeployPending(true);
    setDeployEnabled(false);
    const getStakingTokenUrl = await shortenURL(
      encodeURIComponent(formData.getStakingTokenUrl)
    );

    let tokenError;
    if (bonusTokens.length > 0) {
      Promise.all(
        bonusTokens.flatMap(async (token) => {
          if (
            token.address.toLowerCase() === rewardToken?.address.toLowerCase()
          ) {
            const errorMessage =
              'Bonus token cannot be the same as Reward token, please try again.';
            toast.closeAll();
            toast({
              duration: null,
              isClosable: true,
              position: 'bottom-right',
              // @ts-ignore
              render: ({ onClose }) => (
                <TxnError
                  onClose={onClose}
                  modal={modal}
                  errorMessage={errorMessage}
                  error={errorMessage}
                />
              )
            });
            tokenError = true;
          }

          if (
            token.address.toLowerCase() === stakingToken?.address.toLowerCase()
          ) {
            const errorMessage =
              'Bonus token cannot be the same as Staking token, please try again.';
            toast.closeAll();
            toast({
              duration: null,
              isClosable: true,
              position: 'bottom-right',
              // @ts-ignore
              render: ({ onClose }) => (
                <TxnError
                  onClose={onClose}
                  modal={modal}
                  errorMessage={errorMessage}
                  error={errorMessage}
                />
              )
            });
            tokenError = true;
          }
        })
      );
    }

    if (!tokenError) {
      const programStartTime = new Date(formData.startTime).getTime();

      const deployResults = await deployAludel(
        formData.name,
        getStakingTokenUrl,
        stakingToken!.address,
        rewardToken!.address,
        bonusTokens,
        crucibleFactoryAddress,
        programStartTime / 1000,
        formData.scalingFloor,
        formData.scalingCeiling,
        formData.scalingDays,
        formData.templateId,
        formData.templateName
      );

      if (deployResults && deployResults.aludelAddress) {
        toast.closeAll();
        setDeploymentData({
          ...deploymentData,
          ...formData,
          getStakingTokenUrl,
          programAddress: deployResults.aludelAddress,
          ownerAddress: account!,
          stakingToken: stakingToken || deploymentData.stakingToken,
          rewardToken: rewardToken || deploymentData.rewardToken,
          bonusTokens
        });

        if (formData.rewardTokenAmount > 0.0) {
          await fundAludel(
            deployResults.aludelAddress,
            formData.rewardTokenAmount,
            formData.unlockDays,
            deployResults.aludelDataMethod,
            deployResults.aludelFundMethod,
            deployResults.aludelAbi
          );
        }

        if (bonusTokens.length > 0) {
          await Promise.all(
            bonusTokens.flatMap(async (token) => {
              if (token.amount > 0.0) {
                await fundBonusToken(
                  deployResults.aludelAddress,
                  token,
                  deployResults.aludelDataMethod,
                  deployResults.aludelAbi
                );
              }
            })
          );
        }
        toast.closeAll();
        toast({
          duration: null,
          isClosable: true,
          position: 'bottom-right',
          render: () => (
            <TxnPendingApprovals description='Preparing summary, please wait...' />
          )
        });

        await synchronizeFirebase(chainId, deployResults.aludelAddress);

        toast.closeAll();

        nextStep();
      }
    }
    setDeployPending(false);
    setDeployEnabled(true);
  };

  if (tried && !account) {
    return (
      <>
        <Flex
          justifyContent='center'
          alignItems='center'
          py={10}
          flexDir='column'
        >
          <Heading size='lg' textAlign='center' mb={4}>
            Connect your wallet to deploy a reward program.
          </Heading>
          <Button
            size='lg'
            rightIcon={<VscLink />}
            onClick={() => openModal(ModalType.connectWallet)}
          >
            Connect Wallet
          </Button>
        </Flex>
      </>
    );
  }

  return (
    <FormProvider {...methods}>
      <>
        <SelectAludelTemplate />
        <SetMetadata register={register} errors={errors} />
        <SelectTokens
          setStakingToken={setStakingToken}
          rewardToken={rewardToken}
          setRewardToken={setRewardToken}
          bonusTokens={bonusTokens}
          setBonusTokens={setBonusTokens}
          register={register}
          errors={errors}
        />
        <Parameters />
        <Flex
          justifyContent='space-between'
          alignItems={['space-between', 'center']}
          flexDirection={['column', 'row']}
        >
          <Text mb={[4, 0]} color='gray.200' fontStyle='italic'>
            Note: clicking Deploy will trigger multiple transactions
          </Text>
          <Button
            disabled={!deployEnabled}
            isLoading={deployPending}
            loadingText='Deploying, do not leave this screen'
            onClick={handleSubmit((d) => onDeploy(d))}
          >
            Deploy
          </Button>
        </Flex>
      </>
    </FormProvider>
  );
};

export default Deployment;
